import type { ForwardedRef, ReactNode } from 'react';

import { forwardRef, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@progress/kendo-react-buttons';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { useComponentKey } from '@ricoh/ria.client.core/hooks/app/component';

import styles from './_.module.scss';

import type { DialogActionProps, DialogActionRef } from './_.type';

import useDialogActionHooks from './_.hook';

/** DialogAction. */
export default memo(
  forwardRef(function DialogAction(props: DialogActionProps, ref: ForwardedRef<DialogActionRef>): ReactNode {
    const {
      state: [show],
      title,
      minWidth,
      children,
      actionButtonsSettings = {},
    } = props;

    const { t } = useTranslation();

    const componentKey = useComponentKey();

    const { refs, states, events } = useDialogActionHooks(props, ref);

    if (!show) {
      return null;
    }

    return (
      <Dialog
        ref={refs.dialogRef}
        title={title ?? t(`confirm`)}
        minWidth={minWidth}
        className={styles.container}
        onClose={events.onCloseDialog}
        closeIcon={states.showCloseButton}
      >
        {children}

        {states.buttons.length > 0 && (
          <DialogActionsBar layout={actionButtonsSettings.layout ?? 'end'}>
            {states.buttons.map(({ buttonProps, ...dialogActionButtonProps }, index) => (
              <Button
                key={componentKey.childKey('action-button', index)}
                ref={refs.buttonsRef.ref(index)}
                {...buttonProps}
                themeColor={buttonProps?.themeColor ?? ('primary' in dialogActionButtonProps && dialogActionButtonProps.primary ? 'primary' : 'base')}
                size={buttonProps?.size ?? 'small'}
                style={{
                  minWidth: actionButtonsSettings.minWidth ?? 80,
                  ...buttonProps?.style,
                }}
              />
            ))}
          </DialogActionsBar>
        )}
      </Dialog>
    );
  }),
);
